import React, { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Grid, Card, CardContent
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiFetch } from '../api';

const DataRepairTool = () => {
  const [tab, setTab] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [fixing, setFixing] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, label: '' });

  // ── Analyze ────────────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError('');
    setSuccessMsg('');
    setReport(null);
    try {
      const res = await apiFetch('/api/data-repair/analyze');
      if (res.ok) {
        setReport(await res.json());
        setAnalyzed(true);
      } else {
        setError('Analyze करताना त्रुटी आली.');
      }
    } catch {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Fix ────────────────────────────────────────────────────────────────────
  const handleFix = async (endpoint, label) => {
    setFixing(label);
    setError('');
    setSuccessMsg('');
    try {
      const res = await apiFetch(`/api/data-repair/${endpoint}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(data.message);
        handleAnalyze(); // re-analyze
      } else {
        setError(data.error || 'Fix करताना त्रुटी आली.');
      }
    } catch {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setFixing('');
    }
  };

  // ── Delete Orphan ──────────────────────────────────────────────────────────
  const handleDeleteOrphan = async (id) => {
    setError('');
    try {
      const res = await apiFetch(`/api/data-repair/orphan-transaction/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(data.message);
        handleAnalyze();
      } else {
        setError(data.error || 'Delete करताना त्रुटी आली.');
      }
    } catch {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    }
  };

  // ── Bulk Delete ────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setFixing('bulk');
    setError('');
    try {
      const res = await apiFetch('/api/data-repair/orphan-transactions/bulk', { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(data.message);
        handleAnalyze();
      } else {
        setError(data.error || 'Bulk delete करताना त्रुटी आली.');
      }
    } catch {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setFixing('');
      setConfirmDialog({ open: false, action: null, label: '' });
    }
  };

  const openConfirm = (action, label) => setConfirmDialog({ open: true, action, label });

  const totalIssues = report
    ? (report.summary.customer_issues + report.summary.material_issues +
       report.summary.orphan_txns + report.summary.orphan_returns +
       (report.summary.orphan_ledgers || 0))
    : 0;

  // ── Summary Card ──────────────────────────────────────────────────────────
  const SummaryCard = ({ title, count, color, bg }) => (
    <Card sx={{ bgcolor: bg, borderLeft: `5px solid ${color}`, borderRadius: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" sx={{ color: '#555', fontWeight: 'bold' }}>{title}</Typography>
        <Typography variant="h4" sx={{ color, fontWeight: 'bold' }}>{count}</Typography>
      </CardContent>
    </Card>
  );

  // ── Diff Chip ─────────────────────────────────────────────────────────────
  const DiffChip = ({ diff }) => (
    <Chip
      label={`${diff > 0 ? '+' : ''}${diff}`}
      size="small"
      sx={{
        backgroundColor: diff !== 0 ? '#ffebee' : '#e8f5e9',
        color: diff !== 0 ? '#c62828' : '#2e7d32',
        fontWeight: 'bold'
      }}
    />
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#B71C1C' }}>
            🔧 डेटा Repair Tool
          </Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>
            Balance, Stock Recalculate करा आणि Orphaned Entries शोधा व साफ करा
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={analyzing ? <CircularProgress size={18} color="inherit" /> : <AnalyticsIcon />}
          onClick={handleAnalyze}
          disabled={analyzing}
          sx={{ backgroundColor: '#37474F', '&:hover': { backgroundColor: '#263238' }, px: 3 }}
        >
          {analyzing ? 'Analyze होत आहे...' : '🔍 Analyze करा'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {!analyzed && !analyzing && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '2px dashed #ccc' }}>
          <Typography variant="h6" sx={{ color: '#888', mb: 1 }}>📋 आधी Analyze करा</Typography>
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            वरील "Analyze करा" बटण दाबा म्हणजे कोणत्या entries चुकीच्या आहेत ते दिसेल.
          </Typography>
        </Paper>
      )}

      {report && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}>
              <SummaryCard title="ग्राहक Balance समस्या" count={report.summary.customer_issues} color="#d32f2f" bg="#ffebee" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <SummaryCard title="साहित्य Stock समस्या" count={report.summary.material_issues} color="#e65100" bg="#fff3e0" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <SummaryCard title="Orphaned Transactions" count={report.summary.orphan_txns} color="#6a1b9a" bg="#f3e5f5" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <SummaryCard title="Orphaned Returns" count={report.summary.orphan_returns} color="#1565c0" bg="#e3f2fd" />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <SummaryCard title="अडकलेली खाती (Orphans)" count={report.summary.orphan_ledgers || 0} color="#c2185b" bg="#fce4ec" />
            </Grid>
          </Grid>

          {totalIssues === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              <strong>सर्व काही बरोबर आहे!</strong> कोणतीही discrepancy किंवा orphaned entry सापडली नाही. 🎉
            </Alert>
          )}

          {/* Tabs */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}
              textColor="inherit" indicatorColor="primary">
              <Tab label={`Balance / Stock Fix (${report.summary.customer_issues + report.summary.material_issues})`} />
              <Tab label={`Orphaned Entries (${report.summary.orphan_txns + report.summary.orphan_returns + (report.summary.orphan_ledgers || 0)})`} />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {/* TAB 1: Balance/Stock */}
              {tab === 0 && (
                <Box>
                  {/* Customer Discrepancies */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        ग्राहक Balance Discrepancies ({report.customer_discrepancies.length})
                      </Typography>
                      <Button variant="contained" size="small"
                        startIcon={fixing === 'fix-customers' ? <CircularProgress size={14} color="inherit" /> : <BuildIcon />}
                        disabled={!!fixing || report.customer_discrepancies.length === 0}
                        onClick={() => openConfirm(() => handleFix('fix-customers', 'fix-customers'), 'सर्व ग्राहकांचे Balance Recalculate करायचे?')}
                        sx={{ backgroundColor: '#d32f2f' }}>
                        ✅ सर्व ग्राहक Balance Fix करा
                      </Button>
                    </Box>
                    {report.customer_discrepancies.length === 0 ? (
                      <Alert severity="success">सर्व ग्राहकांचे balance बरोबर आहे. ✅</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#ffebee' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>ग्राहक</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>मोबाईल</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>DB मधील Balance</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>खरे Balance</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>फरक</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.customer_discrepancies.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B5E20' }}>{row.name}</TableCell>
                                <TableCell>{row.mobile || '---'}</TableCell>
                                <TableCell align="right">₹ {row.stored_balance.toLocaleString('en-IN')}</TableCell>
                                <TableCell align="right" sx={{ color: '#1565c0', fontWeight: 'bold' }}>₹ {row.calculated_balance.toLocaleString('en-IN')}</TableCell>
                                <TableCell align="center"><DiffChip diff={row.diff} /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Material Discrepancies */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                        साहित्य Stock Discrepancies ({report.material_discrepancies.length})
                      </Typography>
                      <Button variant="contained" size="small"
                        startIcon={fixing === 'fix-materials' ? <CircularProgress size={14} color="inherit" /> : <BuildIcon />}
                        disabled={!!fixing || report.material_discrepancies.length === 0}
                        onClick={() => openConfirm(() => handleFix('fix-materials', 'fix-materials'), 'सर्व साहित्यांचे Stock Recalculate करायचे?')}
                        sx={{ backgroundColor: '#e65100' }}>
                        ✅ सर्व Material Stock Fix करा
                      </Button>
                    </Box>
                    {report.material_discrepancies.length === 0 ? (
                      <Alert severity="success">सर्व साहित्यांचे stock बरोबर आहे. ✅</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#fff3e0' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>साहित्य</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>DB मधील Stock</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>खरे Stock</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>फरक</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.material_discrepancies.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell sx={{ fontWeight: 'bold' }}>{row.name}</TableCell>
                                <TableCell align="center">{row.stored_stock} नग</TableCell>
                                <TableCell align="center" sx={{ color: '#1565c0', fontWeight: 'bold' }}>{row.calculated_stock} नग</TableCell>
                                <TableCell align="center"><DiffChip diff={row.diff} /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Box>
              )}

              {/* TAB 2: Orphaned Entries */}
              {tab === 1 && (
                <Box>
                  {/* Orphaned Ledger Accounts Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                          अडकलेली खाते मास्टर्स / Orphaned Ledger Accounts ({(report.orphaned_ledgers || []).length})
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          ग्राहक/व्यापारी डिलीट केले आहेत पण त्यांची खाती खाते मास्टरमध्ये शिल्लक आहेत
                        </Typography>
                      </Box>
                      {(report.orphaned_ledgers || []).length > 0 && (
                        <Button variant="contained" size="small"
                          startIcon={fixing === 'fix-ledgers' ? <CircularProgress size={14} color="inherit" /> : <BuildIcon />}
                          disabled={!!fixing}
                          onClick={() => openConfirm(() => handleFix('fix-ledgers', 'fix-ledgers'), 'सर्व अडकलेली (Orphaned) खाती खाते मास्टरमधून हटवायची?')}
                          sx={{ backgroundColor: '#c2185b', '&:hover': { backgroundColor: '#ad1457' } }}>
                          🧹 अडकलेली खाती दुरुस्त करा
                        </Button>
                      )}
                    </Box>

                    {(!report.orphaned_ledgers || report.orphaned_ledgers.length === 0) ? (
                      <Alert severity="success" sx={{ mb: 2 }}>कोणतीही अडकलेली खाती नाहीत. ✅</Alert>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#fce4ec' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>खात्याचे नाव</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>प्रकार (Table)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>मूळ रेकॉर्ड ID</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>तपशील</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.orphaned_ledgers.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell sx={{ fontWeight: 'bold', color: '#c2185b' }}>{row.name}</TableCell>
                                <TableCell><Chip label={row.linked_table} size="small" variant="outlined" /></TableCell>
                                <TableCell>#{row.linked_id}</TableCell>
                                <TableCell sx={{ color: '#c62828', fontSize: '0.78rem' }}>{row.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Orphaned Transactions Section */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#6a1b9a' }}>
                          Orphaned / Doubtful Entries ({report.orphaned_transactions.length + report.orphaned_return_txns.length})
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          ज्या entries चा challan किंवा issue transaction DB मध्ये नाही त्या
                        </Typography>
                      </Box>
                      {(report.orphaned_transactions.length + report.orphaned_return_txns.length) > 0 && (
                        <Button variant="contained" color="error" size="small"
                          startIcon={fixing === 'bulk' ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                          disabled={!!fixing}
                          onClick={() => openConfirm(handleBulkDelete, 'सर्व Orphaned entries delete करायच्या? (Undoable नाही!)')}>
                          🗑️ सर्व Orphans Delete करा
                        </Button>
                      )}
                    </Box>

                    {(report.orphaned_transactions.length + report.orphaned_return_txns.length) === 0 ? (
                      <Alert severity="success">कोणत्याही orphaned entries नाहीत. ✅</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#f3e5f5' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Txn ID</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>ग्राहक</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>साहित्य</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>तारीख</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>कारण</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...report.orphaned_transactions, ...report.orphaned_return_txns].map((row) => (
                              <TableRow key={row.id} hover sx={{ backgroundColor: '#fff8ff' }}>
                                <TableCell>#{row.id}</TableCell>
                                <TableCell>
                                  <Chip label={row.type} size="small"
                                    sx={{ backgroundColor: row.type === 'Issue' ? '#ffebee' : '#e3f2fd', fontWeight: 'bold', fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell>{row.customer_name}</TableCell>
                                <TableCell>{row.material_name}</TableCell>
                                <TableCell>{row.date}</TableCell>
                                <TableCell sx={{ color: '#c62828', fontSize: '0.78rem' }}>
                                  <WarningAmberIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: '#e65100' }} />
                                  {row.reason}
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" color="error"
                                    onClick={() => openConfirm(() => handleDeleteOrphan(row.id), `Transaction #${row.id} delete करायचा?`)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, label: '' })}>
        <DialogTitle sx={{ backgroundColor: '#B71C1C', color: '#fff' }}>⚠️ पुष्टी करा</DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Typography>{confirmDialog.label}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, label: '' })} variant="outlined">रद्द करा</Button>
          <Button onClick={() => { confirmDialog.action?.(); setConfirmDialog({ open: false, action: null, label: '' }); }}
            variant="contained" color="error">हो, करा</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataRepairTool;
